import React, { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CardBottomOverlay from "./CardBottomOverlay";
import FullScreen from "../assets/fullscreen.svg";
import { config } from "node:process";
import illustratorImg from "../assets/chartillustratorImg.png";
import righarrow from "../../../assets/images/dashboard/dashboard_chart/rightArrowimg.png";
import leftarrow from "../../../assets/images/dashboard/dashboard_chart/leftArrowImg.png";
import { Box, Tooltip as MuiTooltip } from "@mui/material";

// Normalizing data
// Normalizing data
const normalizeData = (data: any[], skills: { key: string }[]) => {
  // If only one skill, no normalization
  if (skills.length >= 1) {
    return data;
  }

  return data.map((item) => {
    const normalizedItem: any = { ...item };
    const total = skills.reduce(
      (sum, skill) => sum + (item[skill.key] ?? 0),
      0
    );

    skills.forEach((skill) => {
      normalizedItem[skill.key] =
        total > 0 ? (item[skill.key] / total) * 100 : 0;
    });

    return normalizedItem;
  });
};

// const CustomXAxisTick = (props: any & { totalTicks: number }) => {
//   const { x, y, payload, totalTicks } = props;
//   const text: string = payload.value;

//   // if only one tick, show full text
//   const isLong = text.length > 12 && totalTicks > 1;
//   const displayText = isLong ? text.slice(0, 12) + "..." : text;

//   return (
//     <g transform={`translate(${x},${y})`}>
//       <title>{text}</title>
//       <text
//         x={0}
//         y={0}
//         dy={16}
//         textAnchor="middle"
//         fill="#666"
//         fontSize={10}
//         fontFamily="Mazzard"
//         style={{ cursor:"pointer" }}
//       >
//         {displayText}
//         {/* {isLong && <title>{text}</title>} */}
//       </text>
//     </g>
//   );
// };

const CustomXAxisTick = (props: any & { totalTicks: number }) => {
  const { x, y, payload, isCandidate, data, totalTicks } = props;
  const text = payload.value;

  // Find the full data row that matches this tick
  const row = data.find((item: any) => item.stage === text);

  // Parse the dateTime string and convert time to IST
  let datePart = "";
  let timePart = "";

  if (row && row.dateTime) {
    try {
      const [date, time] = row.dateTime.split(", ");
      datePart = date;

      const [hours, minutes, seconds] = time.split(":").map(Number);
      let istHours = hours + 5;
      let istMinutes = minutes + 30;

      if (istMinutes >= 60) {
        istHours += 1;
        istMinutes -= 60;
      }
      istHours = istHours % 24;

      const period = istHours >= 12 ? "PM" : "AM";
      const displayHours = istHours % 12 || 12;

      timePart = `${displayHours.toString().padStart(2, "0")}:${istMinutes
        .toString()
        .padStart(2, "0")} ${period}`;
    } catch (error) {
      const [date, time] = row.dateTime.split(", ");
      datePart = date;
      timePart = time;
    }
  }

  // ✅ Show full text if only one tick, else truncate long ones
  const maxLength = 12;
  const isLong = text.length > maxLength && totalTicks > 1;
  const displayText =
    totalTicks === 1 ? text : isLong ? text.slice(0, maxLength) + "..." : text;

  const fontSize = isCandidate ? 10 : 14;

  return (
    <g transform={`translate(${x},${y})`}>
      <MuiTooltip
        title={
          row ? (
            <div><span>
              </span>
              <span>{text}</span><div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr", // key fixed, value flexible
                columnGap: "8px",
                whiteSpace: "pre-line",
              }}
            >
              <span>
                <strong>Date:</strong>
              </span>
              <span>{datePart}</span>

              <span>
                <strong>Time:</strong>
              </span>
              <span>{timePart} IST</span>
            </div></div>
          ) : (
            text
          )
        }
        arrow
        placement="top"
      >
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#666"
          fontSize={10}
          fontFamily="Mazzard"
          style={{ cursor: "pointer" }}
        >
          {displayText}
        </text>
      </MuiTooltip>
    </g>
  );
};






const safeId = (name: string) =>
  name.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-_]/g, "");
const SkillAreaChart: React.FC<{
  title: string;
  data: any[];
  skills: { key: string; label: string; color: string }[];
  pageTitle: string;
  toggle: string;
  bg: any;
  heading: string;
  stroke: any;
}> = ({ title, data, skills, pageTitle, toggle, bg, heading, stroke }) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [xIndex, setXIndex] = useState(0); // starting index
const ITEMS_PER_PAGE = 4; // show 4 X-axis labels at a time
const [disableChartTooltip, setDisableChartTooltip] = useState(false);

// Slice chart data
const paginatedData = data.slice(xIndex, xIndex + ITEMS_PER_PAGE);
const [currentIndex, setCurrentIndex] = useState(0);
const PAGE_SIZE = 10; // number of items to show per step

   // Example: suppose `chartData` is your dataset
const hasNegative = data.some(d =>
  Object.values(d).some(v => typeof v === "number" && v < 0)
);
  const handleOpenOverlay = (data: {
    titles: string;
    chartData?: any;
    toggleOptions?: string[];
    slidebarProgressOptions?: string[];
    slidebarResultsOptions?: string[];
  }) => {
    setIsOverlayOpen(true);
  };
  const renderSquareLegend = (props: any) => {
  const { payload } = props;

  // Sort payload ascending
  const sortedPayload = [...payload].sort((a: any, b: any) => {
    if (typeof a.value === "number" && typeof b.value === "number") {
      return a.value - b.value;
    }
    return String(a.value).localeCompare(String(b.value));
  });

  const MAX_VISIBLE = isOverlayOpen ? 6 : 3;
  const visibleLegends = sortedPayload.slice(0, MAX_VISIBLE);
  const hiddenLegends = sortedPayload.slice(MAX_VISIBLE);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        justifyContent: "flex-start",
        // margin: "30px",
        marginBottom: "20px",
      }}
    >
      {/* Visible legends */}
      {visibleLegends.map((entry: any, index: number) => (
        <div
          key={`item-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              backgroundColor: entry.color,
              borderRadius: 2,
            }}
          />
          <span
            style={{
              fontSize: 12,
              color: "#5c5c5c",
              fontFamily: "Mazzard",
            }}
          >
            {entry.value}
          </span>
        </div>
      ))}

      {/* + more tooltip */}
      {hiddenLegends.length > 0 && (
        <MuiTooltip
          arrow
          placement="bottom-start"
          onOpen={() => setDisableChartTooltip(true)}   // ✅ stop chart tooltip
          onClose={() => setDisableChartTooltip(false)} // ✅ restore chart tooltip
          title={
            <div
              style={{
                maxHeight: "320px",
                overflowY: "auto",
                padding: "6px",
              }}
            >
              {hiddenLegends.map((entry: any, index: number) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: entry.color,
                      borderRadius: 2,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: "#ffffff",
                      fontFamily: "Mazzard",
                    }}
                  >
                    {entry.value}
                  </span>
                </div>
              ))}
            </div>
          }
        >
          <span
            style={{
              fontSize: 12,
              color: "#1a73e8",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            + {hiddenLegends.length} more
          </span>
        </MuiTooltip>
      )}
    </div>
  );
};
  return (
    <div
      style={{
        width: "48%",
        marginBottom: 32,
        background: bg,
        paddingTop: "20px",
        paddingBottom: "20px",
        paddingRight: "20px",
        borderRadius: 8,
        marginTop: "0px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // to vertically align items
          paddingLeft: "30px", // padding on both sides instead of just left
          paddingRight: "20px", // padding on both sides instead of just left
          width: "100%", // ensure the container takes full width
        }}
      >
        <h4
          style={{
            paddingBottom: "10px",
            marginBottom: "0",
            fontFamily: "mazzard",
            fontSize: "16px",
            color: "#2b384a",
            fontWeight: 600,
          }}
        >
          {title}
        </h4>
        <img
          style={{
            height: "20px",
            width: "20px",
            alignItems: "center",
            marginBottom: 0,
            cursor: "pointer",
          }}
          src={FullScreen}
          onClick={() =>
            handleOpenOverlay({
              titles: "Jozuna Skills Mastery",
              toggleOptions: ["Skills Timeline", "Assessments Results"], // dynamic per card
              slidebarProgressOptions: [],
              slidebarResultsOptions: [],
            })
          }
        ></img>
      </div>
      <CardBottomOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
      >
        <div className="engagementHeader">
          <h2
            className="engagementHeader-title"
            style={{
              fontFamily: "Mazzard",
              fontSize: "16px",
              color: "#2b384a",
              fontWeight: 600,
            }}
          >
            {heading}
          </h2>
        </div>

        <div
          className="dashboard-divider"
          style={{ marginTop: "20px", marginBottom: "20px" }}
        />
        <div
          style={{
            marginBottom: 32,
            background: bg,
            paddingTop: "20px",
            paddingBottom: "20px",
            paddingRight: "20px",
            paddingLeft: "20px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <h4
            style={{
              fontFamily: "mazzard",
              fontSize: "16px",
              color: "#2b384a",
              fontWeight: 600,
              paddingLeft: "25px",
            }}
          >
            {title}
          </h4>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems:"center" }}>
  {/* Left Arrow */}
  <img
    src={leftarrow}
    alt="Previous"
    onClick={() => setCurrentIndex((prev) => Math.max(prev - PAGE_SIZE, 0))}
    style={{
      width: "45px",
      height: "45px",
      cursor: currentIndex === 0 ? "not-allowed" : "pointer",
      opacity: currentIndex === 0 ? 0.5 : 1,
    }}
  />
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              // data={data}
                data={data.slice(currentIndex, currentIndex + PAGE_SIZE)}

              // margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                          margin={{ top: 20, right: 20, left: 0, bottom: 0 }}

            >
              <CartesianGrid
                stroke={stroke}
                strokeDasharray="0"
                horizontal={true}
                vertical={false}
              />{" "}
              <XAxis
                dataKey="stage"
                axisLine={false}
                tickLine={false}
                fontSize={14}
                tick={<CustomXAxisTick  totalTicks={data.length}
                      // isCandidate={isCandidate}
                      data={data}/>}
                interval={0}
                padding={{ left: 50,right:20 }}
              />
             
<YAxis
  domain={hasNegative ? [-100, 100] : [0, 100]}
  tickCount={hasNegative ? 9 : 5}
  interval={0}
  ticks={
    hasNegative
      ? Array.from({ length: 9 }, (_, i) => -100 + i * 25) // [-100 ... 100]
      : Array.from({ length: 5 }, (_, i) => i * 25)       // [0,25,50,75,100]
  }

  axisLine={false}
  tickLine={false}
  tick={({ x, y, payload }) => {
    const isZero = payload.value === 0;
    return (
      <text
        x={x}
        y={y + 4}
        textAnchor="end"
        fill="#101828"
        fontSize={14}
        fontWeight={isZero ? "bold" : "normal"}
      >
        {payload.value}
      </text>
    );
  }}
/>
              <Tooltip
                wrapperStyle={{ pointerEvents: "all" }}
                content={({ active, payload, label }) => {
                                       if (disableChartTooltip) return null;

                  if (active && payload && payload.length) {
                    return (
                      <div
                        style={{
                          background: "rgba(51, 51, 51, 0.9)",
                          borderRadius: "10px",
                          padding: "8px",
                          border: "1px solid #ccc",
                          color: "white",
                          maxWidth: "600px", // keeps tooltip width under control
                          maxHeight: "190px",
                          overflowY: "auto",
                        }}
                      >
                        <div
                          style={{ fontWeight: "bold", marginBottom: "5px" }}
                        >
                          {label}
                        </div>
                        {payload.map((entry, index) => (
                          <div
                            key={`tooltip-${index}`}
                            style={{
                              display: "flex",
                              // justifyContent: "space-between",
                              gap: "20px",
                              alignItems: "center",
                              marginBottom: "4px",
                            }}
                          >
                            {/* Left side: color box + name */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start", // 🔹 top-align square with first line
                                flex: "1",
                                minWidth: 0, // allows text wrapping
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  backgroundColor: entry.color,
                                  marginRight: "6px",
                                  marginTop: "6px", // 🔹 optional: nudge down a bit for better alignment
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  color: entry.color,
                                  fontSize: "14px",
                                  fontFamily: "Mazzard",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "normal",
                                  // maxWidth: "140px", // control wrapping width
                                }}
                              >
                                {entry.name}
                              </span>
                            </div>

                            {/* Right side: fixed score box */}
                            <span
                              style={{
                                backgroundColor: entry.color,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontFamily: "Mazzard",
                                fontSize: "12px",
                                width: "35px",
                                borderRadius: "6px",
                                height: "35px",
                                marginLeft: "8px",
                                flexShrink: 0, // prevents shrinking
                              }}
                            >
                              {entry.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {pageTitle !== "Overall Progress" && (
                <Legend
                  verticalAlign="top"
                  align="left"
                  content={renderSquareLegend}
                />
              )}
              {toggle == "Skills Timeline" && (
                <Legend
                  verticalAlign="top"
                  align="left"
                  content={renderSquareLegend}
                />
              )}
              <defs>
                {skills.map((skill) => {
                  const id = safeId(skill.key);
                  return (
                    <linearGradient
                      key={id}
                      id={`color-${id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={skill.color}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={skill.color}
                        stopOpacity={0.3}
                      />
                    </linearGradient>
                  );
                })}
              </defs>
              {skills.map((skill) => {
                const id = safeId(skill.key);
                return (
                  <Area
                  
                    key={skill.key}
                    type="linear"
                    dataKey={skill.key}
                    stroke={skill.color}
                    // strokeWidth={2}

                    // fill={`url(#color-${id})`}
                    fill={skill.color}
                    fillOpacity={0.3}
                    dot={{ r: 4 }}
                    connectNulls
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
            <img
    src={righarrow}
    alt="Next"
    onClick={() =>{
       if(data.length>10)
      setCurrentIndex((prev) =>
        Math.min(prev + PAGE_SIZE, data.length - PAGE_SIZE)
      )}
    }
    style={{
      width: "45px",
      height: "45px",
      cursor: currentIndex + PAGE_SIZE >= data.length ? "not-allowed" : "pointer",
      opacity: currentIndex + PAGE_SIZE >= data.length ? 0.5 : 1,
    }}
  />
          </div>
        </div>
      </CardBottomOverlay>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

      <img
  src={leftarrow}
  alt="Previous"
  onClick={() => setXIndex((prev) => Math.max(prev - 1, 0))}
  style={{
    width: "40px",
    height: "40px",
    cursor: xIndex === 0 ? "not-allowed" : "pointer",
    opacity: xIndex === 0 ? 0.5 : 1,
  }}
/>

      <ResponsiveContainer
        width="100%"
        height={280}
        style={{ marginRight: "20px" }}
      >
        <AreaChart
          // data={data}
          data={paginatedData}
          // margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
            margin={{ top: 20, right: 20, left: 0, bottom: 0 }}

        >

          <CartesianGrid
            stroke={stroke}
            strokeDasharray="0"
            horizontal={true}
            vertical={false}
          />{" "}
          {/* <XAxis
            dataKey="stage"
            axisLine={false}
            tickLine={false}
            fontSize={14}
            tick={<CustomXAxisTick />}
          /> */}
          <XAxis
            dataKey="stage"
            axisLine={false}
            tickLine={false}
            fontSize={12}
            tick={<CustomXAxisTick  totalTicks={data.length}
                      // isCandidate={isCandidate}
                      data={data} />}
            interval={0} //ensures every tick is shown
                padding={{ left: 50,right:20 }}
          />
         

<YAxis
  domain={hasNegative ? [-100, 100] : [0, 100]}
  tickCount={hasNegative ? 9 : 5}
  interval={0}
  ticks={
    hasNegative
      ? Array.from({ length: 9 }, (_, i) => -100 + i * 25) // [-100 ... 100]
      : Array.from({ length: 5 }, (_, i) => i * 25)       // [0,25,50,75,100]
  }
  axisLine={false}
  tickLine={false}
  tick={({ x, y, payload }) => {
    const isZero = payload.value === 0;
    return (
      <text
        x={x}
        y={y + 4}
        textAnchor="end"
        fill="#101828"
        fontSize={14}
        fontWeight={isZero ? "bold" : "normal"}
      >
        {payload.value}
      </text>
    );
  }}
/>

          <Tooltip
            wrapperStyle={{ pointerEvents: "all" }}
            content={({ active, payload, label }) => {
                                   if (disableChartTooltip) return null;

              if (active && payload && payload.length) {
                return (
                  <div
                    style={{
                      background: "rgba(51, 51, 51, 0.9)",
                      borderRadius: "10px",
                      padding: "8px",
                      border: "1px solid #ccc",
                      color: "white",
                      maxWidth: "600px", // keeps tooltip width under control
                      maxHeight: "190px",
                      overflowY: "auto",
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                      {label}
                    </div>
                    {payload.map((entry, index) => (
                      <div
                        key={`tooltip-${index}`}
                        style={{
                          display: "flex",
                          // justifyContent: "space-between",
                          gap: "20px",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        {/* Left side: color box + name */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start", // 🔹 top-align square with first line
                            flex: "1",
                            minWidth: 0, // allows text wrapping
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "8px",
                              height: "8px",
                              backgroundColor: entry.color,
                              marginRight: "6px",
                              marginTop: "6px", // 🔹 optional: nudge down a bit for better alignment
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: entry.color,
                              fontSize: "14px",
                              fontFamily: "Mazzard",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                              // maxWidth: "140px", // control wrapping width
                            }}
                          >
                            {entry.name}
                          </span>
                        </div>

                        {/* Right side: fixed score box */}
                        <span
                          style={{
                            backgroundColor: entry.color,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontFamily: "Mazzard",
                            fontSize: "12px",
                            width: "35px",
                            borderRadius: "6px",
                            height: "35px",
                            marginLeft: "8px",
                            flexShrink: 0, // prevents shrinking
                          }}
                        >
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
          {pageTitle !== "Overall Progress" && (
            <Legend
              verticalAlign="top"
              align="left"
              content={renderSquareLegend}
            />
          )}
          {toggle == "Skills Timeline" && (
            <Legend
              verticalAlign="top"
              align="left"
              content={renderSquareLegend}
            />
          )}
          <defs>
            {skills.map((skill) => {
              const id = safeId(skill.key);
              return (
                <linearGradient
                  key={id}
                  id={`color-${id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={skill.color} stopOpacity={0.8} />
                  <stop
                    offset="95%"
                    stopColor={skill.color}
                    stopOpacity={0.3}
                  />
                </linearGradient>
              );
            })}
          </defs>
          {skills.map((skill) => {
            const id = safeId(skill.key);
            return (
              <Area
                key={skill.key}
                type="linear"
                dataKey={skill.key}
                stroke={skill.color}
                // strokeWidth={2}

                // fill={`url(#color-${id})`}
                fill={skill.color}
                fillOpacity={0.3}
                dot={{ r: 4 }}
                connectNulls
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
     
<img
  src={righarrow}
  alt="Next"
  onClick={() =>{
    if(data.length>4)
    setXIndex((prev) => Math.min(prev + 1, data.length - ITEMS_PER_PAGE))
  }}
  style={{
    width: "40px",
    height: "40px",
    cursor: xIndex + ITEMS_PER_PAGE >= data.length ? "not-allowed" : "pointer",
    opacity: xIndex + ITEMS_PER_PAGE >= data.length ? 0.5 : 1,
  }}
/>
  </div>
    </div>
  );
};

const LineChartComponent: React.FC<{
  chartConfigs: any[];
  tittle: string;
  toogle: string;
  heading: string;
}> = ({ chartConfigs, tittle, toogle, heading }) => {
  return (
    <div
      style={{
        background: "transparent",
        paddingTop: "30px",
        height: "100%",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: chartConfigs.length > 0 ? "space-between" : "center",
          gap: "16px",
        }}
      >
        {chartConfigs.length > 0 &&
        chartConfigs.some((c) => c.data && c.data.length > 0) ? (
          chartConfigs.map(
            (config, index) =>
              config.data &&
              config.data.length > 0 && (
                <SkillAreaChart
                  stroke={config.gridColor}
                  key={index}
                  heading={heading}
                  title={config.title}
                  data={normalizeData(config.data, config.skills)}
                  skills={config.skills}
                  pageTitle={tittle}
                  toggle={toogle}
                  bg={config.bgColor}
                />
              )
          )
        ) : (
             <div
  style={{
    fontSize: "18px",
    fontWeight: "500",
    color: "#999",
    display: "flex",
    flexDirection: "column", // stack vertically
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    textAlign: "center",
  }}
>
  <img
    src={illustratorImg}
    style={{ height: "400px", width: "470px", marginBottom: "20px" }}
    alt="No Data"
  />
  <span style={{fontFamily:"MazzardMedium",color:"#272727"}}>No overall data </span>
</div>
        )}
      </div>
    </div>
  );
};

export default LineChartComponent;
